/**
 * LeftRail — persistent sidebar navigation for the logged-in interface.
 *
 * Phase 115 — replaces the old top-bar `UserMenu` on every signed-in
 * page. Public marketing pages keep their own header and are
 * untouched. The rail provides lateral access to every teacher tool
 * grouped into 3 sections: CREATE / ORGANIZE / ASSESS & REPORT.
 *
 * Locked items (tier-gated) stay visible — clicking takes the teacher
 * to the destination, where the existing per-page upgrade prompts
 * surface. The rail itself never duplicates the upgrade logic; it
 * just shows a small lock pill so the teacher knows the feature
 * exists.
 *
 * Persistence: collapse state lives in localStorage so a teacher's
 * preferred rail width survives a page refresh / multi-tab session.
 */

import { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  BookOpenText,
  CalendarRange,
  Calculator,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ClipboardCheck,
  ClipboardList,
  HelpCircle,
  Home,
  Layers,
  Library,
  LifeBuoy,
  ListChecks,
  LogOut,
  Lock,
  ScrollText,
  Shield,
  Sparkles,
  Target,
  UserCircle,
  UserCog,
  Users,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { isPaidUser } from "../../lib/tier";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";

const RAIL_WIDTH = 244;
const RAIL_WIDTH_COLLAPSED = 64;
const STORAGE_KEY = "ae:rail:collapsed";

/**
 * The currently active route is highlighted with a mint accent on
 * the left edge of the link — the same hue used for primary actions
 * across the app so a teacher's eye learns the convention quickly.
 */
const _isActive = (pathname, search, hash, item) => {
  if (!item.to) return false;
  const [path, query] = item.to.split("?");
  if (pathname !== path) return false;
  // View-based routes (e.g. /dashboard?view=planner) must match the
  // current ?view query param so My Library and Planner don't both
  // light up when the teacher is on /dashboard.
  if (query) {
    const target = new URLSearchParams(query);
    const current = new URLSearchParams(search);
    for (const [k, v] of target) {
      if (current.get(k) !== v) return false;
    }
  } else if (path === "/dashboard" && new URLSearchParams(search).has("view")) {
    // Home shouldn't light up while the teacher is on a sub-view.
    return false;
  }
  // Anchor links (Coverage Tracker → /long-range-plan#coverage) only
  // active when the URL hash matches too.
  if (item.hash && item.hash !== hash) return false;
  if (!item.hash && hash && pathname === path && item.exact) return false;
  return true;
};

/**
 * _hasFeature(user, lockKey) — does the user's plan grant the feature
 * behind this rail item? The rail reads the ACTUAL flags the backend
 * granted, delivered in the /auth/me payload at
 * `user.subscription.feature_flags` (see routes/auth.py:165). There is
 * NO hardcoded tier-to-feature map here — Lanes 1/2 changed entitlements
 * and this keeps the rail in lockstep with them automatically.
 *
 * `LOCK_KEY_TO_FLAG` maps a rail item's `lockKey` to its backend flag.
 * Two are not 1:1 by name: `long_range_plan` is gated by PLANNER (it has
 * no flag of its own), and `gradebook` maps to the GRADEBOOK flag.
 */
const LOCK_KEY_TO_FLAG = {
  math_engine: "math_engine",
  individual_student_plan: "individual_student_plan",
  planner: "planner",
  long_range_plan: "planner",
  report_card_builder: "report_card_builder",
  gradebook: "gradebook",
  coverage: "coverage",
};

const _hasFeature = (user, key) => {
  if (!user) return false;
  if (user.is_admin) return true;
  const flag = LOCK_KEY_TO_FLAG[key] || key;
  // has_math_engine_access is an admin-assigned, tier-independent override
  // (unchanged from prior behaviour).
  if (flag === "math_engine" && user.has_math_engine_access) return true;
  const flags = user.subscription?.is_active
    ? user.subscription.feature_flags || []
    : [];
  return flags.includes(flag);
};

// Standalone "Home" entry sits ABOVE the CREATE group — login lands
// here, and it holds the skills & curriculum coverage overview +
// the daily Skill in the Wild card. Phase 115.1 spec — own entry,
// not part of any group.
const HOME_ITEM = { key: "home", to: "/dashboard", label: "Home", icon: Home, exact: true };

const SECTIONS = [
  {
    key: "create",
    label: "Create",
    items: [
      // Lane 4 (Feb 2026) — items visible to signed-in FREE users, but
      // wrapped in the `paidLock` gate below so PAID users see them
      // fully unlocked and FREE users see them muted + linking to
      // /pricing. `freeOnly` items are hidden for paid users.
      { key: "generator", to: "/generator", label: "Generator", icon: Sparkles, paidLock: true },
      { key: "math_engine", to: "/math-engine", label: "Math Engine", icon: Calculator, lockKey: "math_engine", paidLock: true },
      { key: "individual_student_plan", to: "/individual-student-plan", label: "Individual Student Plan", icon: UserCog, lockKey: "individual_student_plan", paidLock: true },
      // Lane 4 — "My Free Lessons" moved from Organize → Create so it
      // sits alongside "Classroom Support" as one of the two live
      // destinations for a signed-in FREE user.
      { key: "my_free_lessons", to: "/my-free-lessons", label: "My Free Lessons", icon: Sparkles, freeOnly: true },
      { key: "classroom_support", to: "/classroom-support", label: "Classroom Support", icon: LifeBuoy },
    ],
  },
  {
    key: "organize",
    label: "Organize",
    items: [
      { key: "library", to: "/dashboard?view=library", label: "My Library", icon: Library, paidLock: true },
      // Community: stays LIVE for every paid tier including Basic
      // (Community has no `lockKey`, so `_hasFeature` returns true).
      // `paidLock: true` triggers the muted+/pricing branch ONLY for
      // free users — Basic/Standard/Pro/Pro+ pass through unchanged.
      // Feb 2026 (mid-month) — the Lane 5 free-user browse entry
      // (`community_library_free`) was retired; free users see only
      // the standard locked entry that routes to /pricing.
      { key: "community_library", to: "/dashboard?view=community", label: "Community Library", icon: Users, paidLock: true },
      { key: "coverage", to: "/dashboard?view=tracker", label: "Coverage Tracker", icon: Target, lockKey: "coverage", paidLock: true },
      { key: "long_range_plan", to: "/long-range-plan", label: "Long Range Plan", icon: CalendarRange, lockKey: "long_range_plan", paidLock: true },
      { key: "planner", to: "/dashboard?view=planner", label: "Planner", icon: ListChecks, lockKey: "planner", paidLock: true },
    ],
  },
  {
    key: "assess",
    label: "Assess & Report",
    items: [
      { key: "gradebook", to: "/gradebook", label: "Gradebook", icon: ClipboardCheck, lockKey: "gradebook", paidLock: true },
      { key: "build_summative", to: "/assess/build", label: "Build a Summative", icon: ClipboardList, lockKey: "gradebook", paidLock: true },
      { key: "report_card_builder", to: "/dashboard?view=report-cards", label: "Report Card Builder", icon: ScrollText, lockKey: "report_card_builder", paidLock: true },
    ],
  },
];


export const LeftRail = ({ collapsed, onToggleCollapse }) => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const pathname = location.pathname;
  const search = location.search;
  const hash = location.hash;

  // Lane 4 — canonical single-source paid-vs-free signal. Consumed by
  // both the freeOnly hide check and the paidLock free-gate below.
  const paidUser = isPaidUser(user);

  // Stable items array — `useMemo` so a route change doesn't rebuild
  // every section every render.
  const sections = useMemo(() => SECTIONS, []);

  // Esc closes the mobile drawer; on desktop it's a no-op because
  // the rail is a sibling, not an overlay.
  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape") {
        document.dispatchEvent(new CustomEvent("ae:rail:close-mobile"));
      }
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, []);

  if (!user) return null;

  return (
    <aside
      data-testid="app-left-rail"
      data-collapsed={collapsed ? "true" : "false"}
      className="flex flex-col bg-[#1c2d3d] text-white border-r border-[#1c2d3d]/40 h-[100dvh] md:h-screen sticky top-0 z-30 overflow-hidden transition-[width] duration-200 ease-out"
      style={{ width: collapsed ? RAIL_WIDTH_COLLAPSED : RAIL_WIDTH }}
    >
      {/* Brand mark — uses the actual Atomic Edu logo asset, matching
          the public marketing header. The placeholder mint "A" badge
          was a stand-in during Phase 115; restored to the real logo
          per Phase 115.5 feedback. */}
      <div className="flex items-center justify-between px-3 h-14 border-b border-white/10 shrink-0">
        <Link
          to="/dashboard"
          className="flex items-center gap-2 text-white font-black tracking-tight no-underline"
          data-testid="rail-brand"
          title="Atomic Edu"
        >
          <img
            src="/brand/atomicedu-logo-mark.png"
            alt="Atomic Edu"
            className="h-8 w-8 object-contain shrink-0"
            data-testid="rail-brand-mark"
          />
          {!collapsed && (
            <span className="text-base">Atomic Edu</span>
          )}
        </Link>
        <button
          type="button"
          onClick={onToggleCollapse}
          className="hidden md:inline-flex h-7 w-7 items-center justify-center rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          aria-label={collapsed ? "Expand rail" : "Collapse rail"}
          title={collapsed ? "Expand" : "Collapse"}
          data-testid="rail-collapse-toggle"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>

      {/* Sections */}
      <nav
        className="flex-1 overflow-y-auto py-3 px-2 space-y-5"
        aria-label="Primary"
      >
        {/* Home — standalone entry above the CREATE group. The teacher
            lands here at login; it's the surface holding the skills &
            curriculum coverage overview + the daily SITW card. */}
        <ul className="space-y-0.5">
          {(() => {
            const Icon = HOME_ITEM.icon;
            const active = _isActive(pathname, search, hash, HOME_ITEM);
            return (
              <li>
                <Link
                  to={HOME_ITEM.to}
                  data-testid={`rail-item-${HOME_ITEM.key}`}
                  data-active={active ? "true" : "false"}
                  className={`group flex items-center ${
                    collapsed ? "justify-center" : "gap-3"
                  } px-2.5 py-2 rounded-md text-sm font-bold transition-colors no-underline ${
                    active
                      ? "bg-white text-[#1c2d3d] shadow-sm"
                      : "text-white/85 hover:bg-white/10 hover:text-white"
                  }`}
                  title={collapsed ? HOME_ITEM.label : undefined}
                >
                  <Icon className={`h-4 w-4 shrink-0 ${active ? "text-[#1c2d3d]" : ""}`} />
                  {!collapsed && <span className="flex-1 truncate">{HOME_ITEM.label}</span>}
                </Link>
              </li>
            );
          })()}
        </ul>
        {sections.map((section) => (
          <div key={section.key}>
            {!collapsed && (
              <p className="px-2 pb-1.5 text-[10px] font-extrabold uppercase tracking-[0.2em] text-white/45">
                {section.label}
              </p>
            )}
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                // Lane 3B — `freeOnly` items (My Saved Lessons) hide
                // entirely for paid users.
                if (item.freeOnly && paidUser) return null;
                const Icon = item.icon;
                const active = _isActive(pathname, search, hash, item);
                // Lane 4 (Feb 2026) — two independent lock signals:
                //   `locked`      — tier-based (existing): item has a
                //                   `lockKey` and the CURRENT user's
                //                   tier doesn't grant it. Fires for
                //                   any user (paid or free) missing
                //                   the flag, e.g. a Basic teacher
                //                   without `math_engine`. Shows the
                //                   lock icon but keeps the item on
                //                   its real route so lower-tier
                //                   paid users can be nudged to
                //                   upgrade to Standard/Pro.
                //   `freeGate`    — Lane 4: item has `paidLock: true`
                //                   AND caller is a signed-in FREE
                //                   user (no active sub, no admin).
                //                   Fires ONLY for the free tier;
                //                   every paid tier passes through
                //                   untouched. Muted styling +
                //                   `/pricing` redirect.
                const locked = !!item.lockKey && !_hasFeature(user, item.lockKey);
                const freeGate = !!item.paidLock && !paidUser;
                const showLock = locked || freeGate;
                const target = freeGate ? "/pricing" : item.to;
                return (
                  <li key={item.key}>
                    <Link
                      to={target}
                      data-testid={`rail-item-${item.key}`}
                      data-active={active ? "true" : "false"}
                      data-locked={showLock ? "true" : "false"}
                      data-free-gate={freeGate ? "true" : "false"}
                      className={`group flex items-center ${
                        collapsed ? "justify-center" : "gap-3"
                      } px-2.5 py-2 rounded-md text-sm font-bold transition-colors no-underline ${
                        active
                          ? "bg-white text-[#1c2d3d] shadow-sm"
                          : freeGate
                            ? "text-white/50 hover:bg-white/5 hover:text-white/70"
                            : "text-white/85 hover:bg-white/10 hover:text-white"
                      }`}
                      title={collapsed ? item.label : undefined}
                    >
                      <Icon className={`h-4 w-4 shrink-0 ${active ? "text-[#1c2d3d]" : ""}`} />
                      {!collapsed && (
                        <>
                          <span className="flex-1 truncate">{item.label}</span>
                          {showLock && (
                            <Lock
                              className={`h-3 w-3 shrink-0 ${
                                active ? "text-[#4a6984]" : "text-white/40 group-hover:text-white/65"
                              }`}
                              aria-label={freeGate ? "Upgrade required" : "Premium feature"}
                            />
                          )}
                        </>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Phase 115.4 — avatar footer with upward popover.
          Single 32px-circle row pinned to the very bottom. The
          previous flat 4-row footer was too vertically heavy on
          mobile. Clicking the avatar opens a floating card upward
          containing Account & Billing / Admin / Help / Sign Out.
          Admin item only renders for `user.is_admin`. */}
      <div className="border-t border-white/15 px-2 py-2 shrink-0">
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="rail-avatar-trigger"
              aria-label="Open account menu"
              className={`w-full flex items-center ${
                collapsed ? "justify-center" : "gap-2.5"
              } px-2 py-2 rounded-md text-sm font-medium transition-colors hover:bg-white/10`}
              title={collapsed ? (user.name || "Account") : undefined}
            >
              <Avatar className="h-8 w-8 shrink-0 ring-1 ring-white/15">
                {user.picture ? (
                  <AvatarImage src={user.picture} alt={user.name || "Account"} />
                ) : null}
                <AvatarFallback className="bg-[#3EB489] text-white text-xs font-medium">
                  {(() => {
                    const parts = (user.name || user.email || "?").trim().split(/\s+/);
                    const a = (parts[0] || "?").charAt(0);
                    const b = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
                    return (a + b).toUpperCase();
                  })()}
                </AvatarFallback>
              </Avatar>
              {!collapsed && (
                <>
                  <span
                    className="flex-1 text-left truncate text-[13px] text-white font-medium"
                    data-testid="rail-avatar-name"
                  >
                    {user.name?.split(" ")[0] || user.email?.split("@")[0] || "Account"}
                  </span>
                  <ChevronUp className="h-4 w-4 shrink-0 text-white/65" />
                </>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="top"
            align="start"
            sideOffset={6}
            className="p-1 border-0 shadow-xl"
            style={{
              width: collapsed ? 220 : RAIL_WIDTH - 16,
              backgroundColor: "#F8F9FA",
              borderRadius: 8,
            }}
            data-testid="rail-avatar-popover"
          >
            <Link
              to="/account"
              data-testid="rail-popover-account"
              className="flex items-center gap-2 h-10 px-4 rounded-md text-[13px] font-medium text-[#1E3A5F] hover:bg-[#3EB489]/10 no-underline transition-colors"
            >
              <UserCircle className="h-4 w-4" /> Account &amp; Billing
            </Link>
            {user.is_admin && (
              <Link
                to="/admin"
                data-testid="rail-popover-admin"
                className="flex items-center gap-2 h-10 px-4 rounded-md text-[13px] font-medium text-[#1E3A5F] hover:bg-[#3EB489]/10 no-underline transition-colors"
              >
                <Shield className="h-4 w-4" /> Admin
              </Link>
            )}
            <Link
              to="/contact"
              data-testid="rail-popover-help"
              className="flex items-center gap-2 h-10 px-4 rounded-md text-[13px] font-medium text-[#1E3A5F] hover:bg-[#3EB489]/10 no-underline transition-colors"
            >
              <HelpCircle className="h-4 w-4" /> Help / Contact
            </Link>
            <div className="my-1 mx-2 border-t border-[#1E3A5F]/10" />
            <button
              type="button"
              onClick={signOut}
              data-testid="rail-popover-signout"
              className="w-full flex items-center gap-2 h-10 px-4 rounded-md text-[13px] font-medium text-[#1E3A5F]/60 hover:bg-[#3EB489]/10 hover:text-[#1E3A5F] transition-colors"
            >
              <LogOut className="h-4 w-4" /> Sign Out
            </button>
          </PopoverContent>
        </Popover>
      </div>
    </aside>
  );
};

// Hook — read + persist the collapse state across reloads.
export const useRailCollapsed = () => {
  const initial = (() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  })();
  return initial;
};

export const persistRailCollapsed = (value) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, value ? "1" : "0");
  } catch {
    /* localStorage unavailable — gracefully no-op */
  }
};

export default LeftRail;
